import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { SentryService } from '@services/sentry.service';
import { SentryFiltersService } from '@services/sentry-filters.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '@env/environment';
import * as Sentry from '@sentry/angular-ivy';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: any): void {
    // Get services via injector to avoid circular dependency issues
    const sentryService = this.injector.get(SentryService);
    const sentryFiltersService = this.injector.get(SentryFiltersService);
    const router = this.injector.get(Router);

    // Don't send errors in development mode
    if (!environment.production) {
      console.error('Error caught by Sentry error handler:', error);
      return;
    }

    // Verify if the error must be ignored
    const sentryEvent = this.convertErrorToSentryEvent(error);
    if (sentryEvent && sentryFiltersService.shouldIgnoreError(sentryEvent)) {
      console.warn(
        'Error skipped by SentryFiltersService:',
        error.message || error,
      );
      return;
    }

    // Add current route to error context
    const context = {
      route: router.url,
      timestamp: new Date().toISOString(),
      component: this.extractComponentInfo(error),
      browser: this.getBrowserInfo(),
      viewportSize: this.getViewportSize(),
    };

    // Capture the error with context
    sentryService.captureException(error, context);
  }

  /**
   * Extract component information from Angular error
   */
  private extractComponentInfo(error: any): string {
    if (error && error.ngDebugContext && error.ngDebugContext.component) {
      const component = error.ngDebugContext.component;
      return component.constructor
        ? component.constructor.name
        : 'Unknown Component';
    }
    return 'No Component Info';
  }

  /**
   * Get browser information
   */
  private getBrowserInfo(): Record<string, string> {
    const userAgent = navigator.userAgent;
    const browserInfo: Record<string, string> = {
      userAgent,
      language: navigator.language,
    };

    // Add browser name and version if available
    if (userAgent.includes('Firefox')) {
      browserInfo['browser'] = 'Firefox';
    } else if (userAgent.includes('Chrome')) {
      browserInfo['browser'] = 'Chrome';
    } else if (userAgent.includes('Safari')) {
      browserInfo['browser'] = 'Safari';
    } else if (userAgent.includes('Edge')) {
      browserInfo['browser'] = 'Edge';
    } else {
      browserInfo['browser'] = 'Other';
    }

    return browserInfo;
  }

  /**
   * Get viewport size
   */
  private getViewportSize(): Record<string, number> {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  /**
   * Converts a JavaScript error to a Sentry Event
   * so it can be processed by SentryFiltersService
   */
  private convertErrorToSentryEvent(error: any): Sentry.Event | null {
    if (!error) {
      return null;
    }

    const event: Sentry.Event = {
      event_id: this.generateEventId(),
      exception: {
        values: [
          {
            type:
              error.name ||
              (error instanceof Error ? error.constructor.name : 'Error'),
            value: error.message || String(error),
            stacktrace: this.extractStacktrace(error),
          },
        ],
      },
    };

    return event;
  }

  /**
   * Generates an event ID for Sentry
   */
  private generateEventId(): string {
    const hexChars = '0123456789abcdef';
    let id = '';
    for (let i = 0; i < 32; i++) {
      id += hexChars[Math.floor(Math.random() * 16)];
    }
    return id;
  }

  /**
   * Extracts stacktrace information from an error
   */
  private extractStacktrace(error: any): { frames: any[] } | undefined {
    if (!error.stack) {
      return undefined;
    }

    try {
      const stackLines = error.stack.split('\n').slice(1);
      const frames = stackLines.map((line: string) => {
        const match = line.match(/at\s+(.*?)\s+\((.*?):(\d+):(\d+)\)/);
        if (match) {
          return {
            function: match[1] || '?',
            filename: match[2] || '?',
            lineno: parseInt(match[3], 10) || 0,
            colno: parseInt(match[4], 10) || 0,
          };
        }
        return { filename: line.trim() };
      });

      return { frames: frames.reverse() };
    } catch (e) {
      return undefined;
    }
  }
}
